import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, Sparkles, User, AlertCircle, Square, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppContext } from "@/lib/onboardingContext";
import { useSubscription, PLAN_LIMITS } from "@/lib/subscriptionContext";
import ReactMarkdown from "react-markdown";
import { formatCityName } from "@/lib/utils";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/business-assistant`;

export default function Assistant() {
  const navigate = useNavigate();
  const { results, onboarding } = useAppContext();
  const { subscription } = useSubscription();
  const plan = subscription?.plan ?? "free";
  const aiLimit = PLAN_LIMITS[plan]?.aiQueries ?? 10;

  const [queryCount, setQueryCount] = useState(0);
  const isAtLimit = queryCount >= aiLimit;

  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: results
        ? `👋 Hi! I'm your AI business assistant. I see you're planning to open a **${results.businessLabel}** in **${formatCityName(results.city)}**.\n\nI can help you with:\n- **Permits & licenses** specific to your city\n- **Budget planning** and cost breakdowns\n- **Staffing** recommendations\n- **Location** advice and rent analysis\n- **Timeline** and next steps\n\nWhat would you like to know?`
        : "👋 Hi! I'm your AI business assistant. I can help you plan your restaurant, café, bar, or bakery opening.\n\nComplete the onboarding first to get personalized advice, or ask me any general question about opening a food & beverage business!",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [followUps, setFollowUps] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const suggestions = results
    ? [
        `What permits do I need in ${formatCityName(results.city)}?`,
        `What are the biggest risks opening in ${formatCityName(results.city)}?`,
        "What staff should I hire first?",
        "What should I do this week?",
      ]
    : [
        "How much does it cost to open a restaurant?",
        "What licenses do I need for a bar?",
        "How do I find the right location?",
        "What are common mistakes to avoid?",
      ];

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, followUps]);

  const buildContext = () => {
    if (!results) return null;
    return {
      businessType: results.businessLabel,
      city: results.city,
      budget: onboarding.budget,
      sqftMin: onboarding.sqftMin,
      sqftMax: onboarding.sqftMax,
      seats: onboarding.seats,
      seatingType: onboarding.seatingType,
      serviceStyle: onboarding.serviceStyle,
      experience: onboarding.experience,
      alcoholType: onboarding.alcoholType,
      spacePreference: onboarding.spacePreference,
      targetOpeningDate: onboarding.targetOpeningDate,
      teamSize: onboarding.teamSize,
      openingHours: onboarding.openingHours,
      daysPerWeek: onboarding.daysPerWeek,
      totalEstimate: results.totalEstimate,
      budgetFit: results.budgetFit,
      cityState: results.city,
    };
  };

  const cancelStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsLoading(false);
  }, []);

  const generateFollowUps = (lastAssistantMsg: string): string[] => {
    // Generate contextual follow-ups based on the conversation
    if (!results) return ["What permits do I need?", "How much does it cost?"];
    
    const city = results.city;
    const biz = results.businessLabel;
    
    if (lastAssistantMsg.toLowerCase().includes("permit") || lastAssistantMsg.toLowerCase().includes("license")) {
      return [`How long does the ${onboarding.alcoholType !== "none" ? "liquor " : ""}license take in ${city}?`, `What's the total permit cost for a ${biz}?`, "What happens if I miss a permit?"];
    }
    if (lastAssistantMsg.toLowerCase().includes("staff") || lastAssistantMsg.toLowerCase().includes("hire")) {
      return ["What should I pay my staff?", "When should I start hiring?", `How many people for a ${biz}?`];
    }
    if (lastAssistantMsg.toLowerCase().includes("budget") || lastAssistantMsg.toLowerCase().includes("cost")) {
      return ["Where can I cut costs?", "What are hidden costs?", "How do I get funding?"];
    }
    if (lastAssistantMsg.toLowerCase().includes("location") || lastAssistantMsg.toLowerCase().includes("rent")) {
      return [`Best neighborhoods in ${city}?`, "What rent-to-revenue ratio is safe?", "Second-gen vs new build?"];
    }
    return [`What risks should I watch for in ${city}?`, "What should I do this week?", `Is my budget realistic for a ${biz}?`];
  };

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading || isAtLimit) return;
    setError(null);
    setFollowUps([]);
    const userMsg: Message = { role: "user", content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    let assistantSoFar = "";
    const upsertAssistant = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && prev.length > newMessages.length) {
          return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantSoFar } : m);
        }
        return [...prev.slice(0, newMessages.length), { role: "assistant", content: assistantSoFar }];
      });
    };

    try {
      // Get user session token for authenticated request
      const { data: { session } } = await (await import("@/integrations/supabase/client")).supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        setError("Please sign in to use the assistant.");
        setIsLoading(false);
        return;
      }

      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ messages: newMessages, businessContext: buildContext() }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setError(err.error || "Something went wrong. Please try again.");
        setIsLoading(false);
        return;
      }

      if (!resp.body) {
        setError("No response received.");
        setIsLoading(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") { streamDone = true; break; }
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) upsertAssistant(content);
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }

      // flush remaining
      if (buffer.trim()) {
        for (let raw of buffer.split("\n")) {
          if (!raw) continue;
          if (raw.endsWith("\r")) raw = raw.slice(0, -1);
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) upsertAssistant(content);
          } catch {}
        }
      }

      setIsLoading(false);
      abortRef.current = null;
      setQueryCount(prev => prev + 1);
      // Generate follow-up suggestions
      if (assistantSoFar) {
        setFollowUps(generateFollowUps(assistantSoFar));
      }
    } catch (e: any) {
      if (e.name === "AbortError") {
        setIsLoading(false);
        return;
      }
      console.error(e);
      setError("Connection error. Please check your internet and try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(results ? "/dashboard" : "/")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-sm font-medium text-foreground">AI Business Assistant</h1>
              <p className="text-xs text-muted-foreground">
                {results ? `Helping you open your ${results.businessLabel}` : "Ask anything about opening your business"}
              </p>
            </div>
          </div>
          <div className="ml-auto text-xs font-medium px-2.5 py-1 rounded-full bg-muted text-muted-foreground">
            {queryCount} of {aiLimit} queries
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="container max-w-2xl py-6 space-y-4">
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}
            >
              {msg.role === "assistant" && (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground rounded-br-md"
                  : "bg-card border border-border text-foreground rounded-bl-md"
              }`}>
                {msg.role === "assistant" ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                ) : (
                  msg.content
                )}
              </div>
              {msg.role === "user" && (
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 mt-1">
                  <User className="w-4 h-4 text-muted-foreground" />
                </div>
              )}
            </motion.div>
          ))}
          {isLoading && messages[messages.length - 1]?.role === "user" && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}

          {/* Follow-up suggestions */}
          <AnimatePresence>
            {followUps.length > 0 && !isLoading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex gap-2 flex-wrap pt-2"
              >
                {followUps.map(q => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="container max-w-2xl pb-2">
          <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        </div>
      )}

      {/* Initial suggestions */}
      {messages.length <= 2 && followUps.length === 0 && (
        <div className="container max-w-2xl pb-2">
          <div className="flex gap-2 flex-wrap">
            {suggestions.map(s => (
              <button
                key={s}
                onClick={() => sendMessage(s)}
                className="text-xs px-3 py-1.5 rounded-full border border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Limit reached */}
      {isAtLimit && (
        <div className="container max-w-2xl pb-2">
          <div className="flex items-center gap-2 text-xs bg-muted rounded-lg px-3 py-2.5">
            <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">You've used all {aiLimit} AI queries this month.</span>
            <Button size="sm" variant="link" className="h-auto p-0 text-xs text-primary" onClick={() => navigate("/pricing")}>
              Upgrade for more →
            </Button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border bg-card">
        <div className="container max-w-2xl py-4">
          <form
            onSubmit={e => { e.preventDefault(); sendMessage(input); }}
            className="flex gap-2"
          >
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask about permits, locations, budget, staffing..."
              className="h-11 flex-1"
            />
            {isLoading ? (
              <Button type="button" size="icon" variant="outline" className="h-11 w-11 shrink-0" onClick={cancelStream}>
                <Square className="w-4 h-4" />
              </Button>
            ) : (
              <Button type="submit" size="icon" className="h-11 w-11 shrink-0" disabled={!input.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
